/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "@app/axios/axiosIns";

// Compliance projects

export const getAllComplianceProjects = createAsyncThunk(
  "compliance/getAllProjects",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/admin/compliance-project", {
        params,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getComplianceProject = createAsyncThunk(
  "compliance/getProject",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/admin/compliance-project/${id}`
      );
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const addComplianceProject = createAsyncThunk(
  "compliance/addProject",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/admin/compliance-project",
        data
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateComplianceProject = createAsyncThunk(
  "compliance/updateProject",
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/admin/compliance-project/${id}`,
        data
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const deleteComplianceProject = createAsyncThunk(
  "compliance/deleteProject",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/admin/compliance-project/${id}`
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

// Saves the full project list in its new display order.
export const reorderComplianceProjects = createAsyncThunk(
  "compliance/reorderProjects",
  async (ids: string[], { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        "/admin/compliance-project/reorder",
        { ids }
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

// Compliance documents

// Saves one project's full document list in its new display order.
export const reorderComplianceDocuments = createAsyncThunk(
  "compliance/reorderDocuments",
  async (
    { projectId, ids }: { projectId: string; ids: string[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.patch(
        "/admin/compliance-document/reorder",
        { compliance_project_id: projectId, ids }
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getComplianceDocuments = createAsyncThunk(
  "compliance/getDocuments",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/admin/compliance-document", {
        params,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const addComplianceDocument = createAsyncThunk(
  "compliance/addDocument",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/admin/compliance-document",
        data
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateComplianceDocument = createAsyncThunk(
  "compliance/updateDocument",
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/admin/compliance-document/${id}`,
        data
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const deleteComplianceDocument = createAsyncThunk(
  "compliance/deleteDocument",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/admin/compliance-document/${id}`
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

interface ComplianceState {
  isLoading: boolean;
  formLoader: boolean;
  documentLoader: boolean;
  projects: any;
  project: any;
  documents: any;
  error: any;
}

const initialState: ComplianceState = {
  isLoading: false,
  formLoader: false,
  documentLoader: false,
  projects: [],
  project: null,
  documents: [],
  error: null,
};

const complianceSlice: any = createSlice({
  name: "compliance",
  initialState,
  reducers: {
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Get all projects
      .addCase(getAllComplianceProjects.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllComplianceProjects.fulfilled, (state, action) => {
        state.isLoading = false;
        state.projects = action.payload?.data;
      })
      .addCase(getAllComplianceProjects.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // Get project
      .addCase(getComplianceProject.pending, (state) => {
        state.isLoading = true;
        state.project = null;
      })
      .addCase(getComplianceProject.fulfilled, (state, action) => {
        state.isLoading = false;
        state.project = action.payload?.data;
      })
      .addCase(getComplianceProject.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // Add / update project
      .addCase(addComplianceProject.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(addComplianceProject.fulfilled, (state, action) => {
        state.formLoader = false;
        state.project = action.payload?.data?.data;
      })
      .addCase(addComplianceProject.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.error.message;
      })
      .addCase(updateComplianceProject.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(updateComplianceProject.fulfilled, (state, action) => {
        state.formLoader = false;
        state.project = action.payload?.data?.data;
      })
      .addCase(updateComplianceProject.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.error.message;
      })
      // Delete project
      .addCase(deleteComplianceProject.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteComplianceProject.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteComplianceProject.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // Get documents
      .addCase(getComplianceDocuments.fulfilled, (state, action) => {
        state.documents = action.payload?.data;
      })
      .addCase(getComplianceDocuments.rejected, (state, action) => {
        state.error = action.error.message;
      })
      // Add / update document
      .addCase(addComplianceDocument.pending, (state) => {
        state.documentLoader = true;
      })
      .addCase(addComplianceDocument.fulfilled, (state) => {
        state.documentLoader = false;
      })
      .addCase(addComplianceDocument.rejected, (state, action) => {
        state.documentLoader = false;
        state.error = action.error.message;
      })
      .addCase(updateComplianceDocument.pending, (state) => {
        state.documentLoader = true;
      })
      .addCase(updateComplianceDocument.fulfilled, (state) => {
        state.documentLoader = false;
      })
      .addCase(updateComplianceDocument.rejected, (state, action) => {
        state.documentLoader = false;
        state.error = action.error.message;
      });
  },
});

export const { resetState } = complianceSlice.actions;
export default complianceSlice.reducer;
